import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CheckScreen, SplitScreen, TipScreen, PayScreen, ApprovedScreen, PaidScreen, SafariChrome, CameraScreen, LoadingScreen } from './QrPayScreens';
import './QrPayFilm.css';

/*
  QR Pay hero film — wordless, Benji-style product motion.
  A phone floats on a soft stage; a finger dot taps through Split Check while the camera
  makes tight, springy crops. Everything is a pure function of the clock, so autoplay,
  pause and jump-to-chapter always land on a real frame. Plays when ≥40% on screen;
  pauses off-screen / hidden tab; reduced motion opens paused on the split beat.
  The script below is authored in "story time" (tt = t + T0).
*/

const T0 = -1.0;       // story time at film start (phone rises in the Camera app)
const T_END = 25.0;    // story time at film end
const D = T_END - T0;  // film length (s)
const RESET = 0.6;     // fade between loops (s)

// Camera (story time): y = % of stage (entry from below), s = zoom, f = focus kept centred (0 top … 1 bottom), rx/ry = tilt (deg)
const K = [
  { t: -1.0, y: 90, s: 1, f: 0.5, rx: 18, ry: -12 },
  { t: -0.85, y: 90, s: 1, f: 0.5, rx: 18, ry: -12 },
  { t: 0.5, y: 0, s: 1, f: 0.5, rx: 4, ry: -7 },
  { t: 1.3, y: 0, s: 1.14, f: 0.5, rx: 2, ry: -4 },
  { t: 2.7, y: 0, s: 1.14, f: 0.56, rx: 0, ry: -3 },
  { t: 3.5, y: 0, s: 1, f: 0.5, rx: 0, ry: -6 },
  { t: 6.9, y: 0, s: 1, f: 0.5, rx: 0, ry: -6 },
  { t: 7.35, y: 0, s: 1.2, f: 0.8, rx: 0, ry: -3 },
  { t: 7.75, y: 0, s: 1.2, f: 0.8, rx: 0, ry: -3 },
  { t: 8.6, y: 0, s: 1.34, f: 0.24, rx: 0, ry: 4 },
  { t: 11.6, y: 0, s: 1.34, f: 0.24, rx: 0, ry: 4 },
  { t: 12.1, y: 0, s: 1.34, f: 0.34, rx: 0, ry: 3 },
  { t: 12.9, y: 0, s: 1.34, f: 0.34, rx: 0, ry: 3 },
  { t: 13.25, y: 0, s: 1.2, f: 0.84, rx: 0, ry: 1 },
  { t: 13.9, y: 0, s: 1.2, f: 0.84, rx: 0, ry: 1 },
  { t: 14.7, y: 0, s: 1.26, f: 0.27, rx: 0, ry: -4 },
  { t: 16.55, y: 0, s: 1.26, f: 0.27, rx: 0, ry: -4 },
  { t: 16.9, y: 0, s: 1.2, f: 0.84, rx: 0, ry: -2 },
  { t: 17.5, y: 0, s: 1.2, f: 0.84, rx: 0, ry: -2 },
  { t: 18.3, y: 0, s: 1.14, f: 0.36, rx: 0, ry: 5 },
  { t: 19.85, y: 0, s: 1.14, f: 0.36, rx: 0, ry: 5 },
  { t: 20.15, y: 0, s: 1.18, f: 0.6, rx: 0, ry: 3 },
  { t: 20.7, y: 0, s: 1.18, f: 0.6, rx: 0, ry: 3 },
  { t: 21.3, y: 0, s: 1, f: 0.5, rx: 0, ry: 0 },
  { t: 23.6, y: 0, s: 1, f: 0.5, rx: 0, ry: -3 },
  { t: 25.0, y: 0, s: 0.95, f: 0.5, rx: 2, ry: -6 },
];

// Screen layers (story time): [start, key, transition, duration]
// push = forward navigation (iOS: in from the right, old page parallaxes left and dims)
// swap = in-place state change (crossfade, no spatial movement)
// launch = app open from the Camera's link pill (scale up from the pill + fade)
const LAYERS = [
  [-1.0, 'camera', 'swap', 0.01],
  [2.62, 'loading', 'launch', 0.45],
  [4.15, 'check', 'swap', 0.4],
  [7.75, 'split', 'push', 0.5],
  [13.6, 'tip', 'push', 0.5],
  [17.25, 'pay', 'push', 0.5],
  [20.75, 'approved', 'swap', 0.45],
  [22.6, 'paid', 'swap', 0.5],
];

// Every tap the finger makes (story time) — keys match data-tap in QrPayScreens
const TAPS = [
  [2.45, 'qrlink'], [7.55, 'split'], [8.9, 'people+'], [9.15, 'people+'], [9.4, 'people+'], [9.65, 'people+'], [10.35, 'people-'], [10.6, 'people-'],
  [12.3, 'payFor+'], [13.35, 'continue'], [15.65, '20%'], [17.0, 'tipContinue'], [18.55, 'card'], [20.25, 'pay'],
];

// Stepper taps on "People at the table": [time, value, button]
const PEOPLE = [[0, 2], [8.9, 3, '+'], [9.15, 4, '+'], [9.4, 5, '+'], [9.65, 6, '+'], [10.35, 5, '-'], [10.6, 4, '-']];
const PRESS_MS = 0.14;
const GLOW = [14.0, 24.2];
// Chapters in story time (labels are for screen readers; the film itself is wordless)
const CHAPTERS_STORY = [['Scan', -1.0], ['Split', 7.0], ['Tip', 13.8], ['Pay', 17.2], ['Done', 20.4]];
export const CHAPTERS = CHAPTERS_STORY.map(([l, s0]) => [l, s0 - T0]);

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const easeOut = (x) => 1 - Math.pow(1 - x, 4);
const lerp = (a, b, x) => a + (b - a) * x;
const fade = (t, [a, b], e = 0.55) => clamp(Math.min((t - a) / e, (b - t) / e));

function camera(t) {
  let i = 0;
  while (i < K.length - 2 && t > K[i + 1].t) i += 1;
  const a = K[i];
  const b = K[i + 1];
  const x = ease(clamp((t - a.t) / (b.t - a.t)));
  const o = {};
  ['y', 's', 'f', 'rx', 'ry'].forEach((k) => { o[k] = lerp(a[k], b[k], x); });
  return o;
}

// Everything the screens need at story time t
function screenState(t) {
  let people = 2;
  let press = null;
  for (const [at, v, btn] of PEOPLE) {
    if (t >= at) {
      people = v;
      if (btn && t < at + PRESS_MS) press = `people${btn}`;
    }
  }
  const checkPress = t >= 7.55 && t < 7.55 + PRESS_MS * 1.5 ? 'split' : null;
  const payFor = t >= 12.35 ? 2 : 1;
  if (t >= 12.3 && t < 12.3 + PRESS_MS) press = 'payFor+';
  let focus = null;
  if (t >= 8.6 && t < 11.9) focus = 'people';
  else if (t >= 11.9) focus = 'payFor';
  if (t >= 13.35 && t < 13.35 + PRESS_MS * 1.5) press = 'continue';

  const tip = t >= 15.75 ? '20%' : null;
  let tipPress = null;
  if (t >= 15.65 && t < 15.65 + PRESS_MS * 1.4) tipPress = '20%';
  if (t >= 17.0 && t < 17.0 + PRESS_MS * 1.5) tipPress = 'continue';

  const typed = clamp((t - 18.75) / 1.1);
  const payPress = t >= 20.25 && t < 20.25 + PRESS_MS * 1.6 ? 'pay' : null;
  const draw = clamp((t - 20.95) / 1.3);
  const load = clamp((t - 2.85) / 1.25);
  return { tt: t, people, payFor, focus, press, checkPress, tip, tipPress, typed, payPress, draw, load: 1 - Math.pow(1 - load, 2) };
}

// Finger dot: where it is, how visible, whether it is pressing (positions in canvas px)
function fingerAt(t, P) {
  if (!P) return null;
  const pos = (key) => P[key] || null;
  let j = TAPS.findIndex(([at]) => at > t);
  if (j === -1) j = TAPS.length;
  const prev = TAPS[j - 1];
  const next = TAPS[j];
  let x; let y; let a = 0;
  const near = (at) => Math.min(1, Math.max(0, 1 - Math.abs(t - at) / 0.18));
  const pressK = Math.max(prev ? near(prev[0] + 0.03) : 0, next ? near(next[0] + 0.03) : 0);
  const appear = (at) => clamp((t - (at - 0.75)) / 0.35);
  const leave = (at) => 1 - clamp((t - (at + 0.45)) / 0.35);
  if (next && (!prev || next[0] - prev[0] > 1.0)) {
    // Arriving from out of frame for an isolated tap (or the very first)
    const p = pos(next[1]);
    if (!p) return null;
    const k = easeOut(appear(next[0]));
    x = p.x + (1 - k) * 46; y = p.y + (1 - k) * 70;
    a = prev ? Math.max(appear(next[0]), leave(prev[0])) : appear(next[0]);
    if (prev && leave(prev[0]) > appear(next[0])) {
      const q = pos(prev[1]);
      if (q) { x = q.x; y = q.y; a = leave(prev[0]); }
    }
  } else if (next && prev) {
    // Travel between taps that are close together
    const p0 = pos(prev[1]); const p1 = pos(next[1]);
    if (!p0 || !p1) return null;
    const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    const travel = clamp(0.22 + dist / 900, 0.22, 0.6);
    const k = ease(clamp((t - (next[0] - 0.08 - travel)) / travel));
    x = lerp(p0.x, p1.x, k); y = lerp(p0.y, p1.y, k) - Math.sin(Math.PI * k) * Math.min(40, dist * 0.15);
    a = 1;
  } else if (prev) {
    const q = pos(prev[1]);
    if (!q) return null;
    x = q.x; y = q.y; a = leave(prev[0]);
  } else return null;
  const ripple = prev && t - prev[0] < 0.55 && t >= prev[0] ? (t - prev[0]) / 0.55 : null;
  return { x, y, a, press: pressK, ripple };
}

// Apple's navigation curve, cubic-bezier(0.32, 0.72, 0, 1)
function bezier(x1, y1, x2, y2) {
  const cx = 3 * x1; const bx = 3 * (x2 - x1) - cx; const ax = 1 - cx - bx;
  const cy = 3 * y1; const by = 3 * (y2 - y1) - cy; const ay = 1 - cy - by;
  const sx = (u) => ((ax * u + bx) * u + cx) * u;
  const sy = (u) => ((ay * u + by) * u + cy) * u;
  return (x) => {
    let u = x;
    for (let i = 0; i < 8; i += 1) {
      const d = (3 * ax * u + 2 * bx) * u + cx;
      if (Math.abs(d) < 1e-6) break;
      u -= (sx(u) - x) / d;
    }
    return sy(clamp(u));
  };
}
const navEase = bezier(0.32, 0.72, 0, 1);

// Incoming page
function layerStyle(type, p) {
  const e = navEase(clamp(p));
  if (type === 'push') return { transform: `translate3d(${(1 - e) * 100}%, 0, 0)`, boxShadow: p < 1 ? '-12px 0 32px rgba(22, 21, 19, .12)' : 'none' };
  if (type === 'launch') return { opacity: clamp(p * 2.2), transform: `scale(${0.86 + 0.14 * e})`, transformOrigin: '50% 66%', borderRadius: `${(1 - e) * 40}px` };
  return { opacity: easeOut(clamp(p)) };
}
// Outgoing page underneath (only moves for a push)
function underStyle(type, p) {
  if (type === 'launch') return { transform: `scale(${1 - 0.04 * navEase(clamp(p))})` };
  if (type !== 'push') return undefined;
  return { transform: `translate3d(${-30 * navEase(clamp(p))}%, 0, 0)` };
}

function renderScreen(key, st) {
  switch (key) {
    case 'camera': return <CameraScreen t={st.tt} />;
    case 'loading': return <LoadingScreen />;
    case 'check': return <CheckScreen press={st.checkPress} />;
    case 'split': return <SplitScreen people={st.people} payFor={st.payFor} focus={st.focus} press={st.press} />;
    case 'tip': return <TipScreen selected={st.tip} press={st.tipPress} />;
    case 'pay': return <PayScreen typed={st.typed} press={st.payPress} />;
    case 'approved': return <ApprovedScreen draw={st.draw} />;
    case 'paid': return <PaidScreen />;
    default: return null;
  }
}

// Off-screen copies of the tappable screens, used once per size to find every tap target
const TapMap = memo(({ ch, onMeasure }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const map = {};
    root.querySelectorAll('[data-screen]').forEach((box) => {
      const o = box.getBoundingClientRect();
      box.querySelectorAll('[data-tap]').forEach((el) => {
        const r = el.getBoundingClientRect();
        map[el.dataset.tap] = { x: r.left - o.left + r.width / 2, y: r.top - o.top + r.height / 2 };
      });
    });
    onMeasure(map);
  }, [ch, onMeasure]);
  return (
    <div ref={ref} className="qpf-tapmap" aria-hidden="true">
      {[<CameraScreen key="q" measure />, <CheckScreen key="c" />, <SplitScreen key="s" people={2} payFor={1} />, <TipScreen key="t" selected="20%" />, <PayScreen key="p" typed={1} />].map((el) => (
        <div key={el.key} data-screen className="qpf-tapmap__screen" style={{ height: ch }}>{el}</div>
      ))}
    </div>
  );
});

export default function QrPayFilm({ teaser = null, active = true }) {
  // teaser = [from, to] film seconds, or 'full': card-thumbnail mode (no controls), playing only while `active`
  const START = Array.isArray(teaser) ? teaser[0] : teaser === 'full' ? 1.5 : 0; // full card loop starts once the phone has settled
  const END = Array.isArray(teaser) ? teaser[1] : D;
  const stageRef = useRef(null);
  const screenRef = useRef(null);
  const deviceRef = useRef(null);
  const [t, setT] = useState(START);
  const [size, setSize] = useState({ w: 1200, h: 700, k: 1, ch: 844, dh: 600 });
  const [playing, setPlaying] = useState(!teaser);
  const [inView, setInView] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [taps, setTaps] = useState(null);
  const tRef = useRef(START);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reduced motion: open paused on the first product beat
  useEffect(() => {
    if (reduce && !teaser) { setPlaying(false); tRef.current = 10.0 - T0; setT(10.0 - T0); }
  }, [reduce, teaser]);

  // Teaser: play while the card is hovered/focused, rewind when it isn't
  useEffect(() => {
    if (!teaser) return;
    if (active && !reduce) setPlaying(true);
    else { setPlaying(false); tRef.current = START; setT(START); }
  }, [teaser, active, reduce, START]);

  // Measure stage + phone screen (screens render at 390 wide and scale in)
  useLayoutEffect(() => {
    const measure = () => {
      const st = stageRef.current; const sc = screenRef.current; const dv = deviceRef.current;
      if (!st || !sc || !dv) return;
      const k = sc.clientWidth / 390;
      setSize({ w: st.clientWidth, h: st.clientHeight, k, ch: sc.clientHeight / k, dh: dv.offsetHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Clock
  useEffect(() => {
    if (!playing || !inView || resetting) return undefined;
    let raf = 0; let last = null;
    const tick = (now) => {
      if (document.hidden) { last = null; raf = requestAnimationFrame(tick); return; }
      if (last != null) tRef.current += Math.min(0.05, (now - last) / 1000);
      last = now;
      if (tRef.current >= END) {
        setResetting(true);
        return;
      }
      setT(tRef.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, inView, resetting, END]);

  // Loop: fade out, rewind, fade in
  useEffect(() => {
    if (!resetting) return undefined;
    const a = setTimeout(() => { tRef.current = START; setT(START); }, RESET * 1000);
    const b = setTimeout(() => setResetting(false), RESET * 1000 + 250);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [resetting, START]);

  const seek = useCallback((nt) => { tRef.current = clamp(nt, 0, D - 0.01); setT(tRef.current); }, []);

  // Dev-only handle for checking frames (stripped from production builds)
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || teaser) return undefined;
    window.__qpf = { seek, pause: () => setPlaying(false), get t() { return tRef.current; } };
    return () => { delete window.__qpf; };
  }, [seek, teaser]);

  const narrow = size.w <= 760;
  const tt = t + T0; // story time
  const cam = camera(tt);
  const s = narrow ? 1 + (cam.s - 1) * 0.85 : cam.s;
  const y = (cam.y * size.h) / 100 - (cam.f - 0.5) * size.dh * (s - 1);
  const st = screenState(tt);

  let cur = 0;
  LAYERS.forEach(([start], i) => { if (tt >= start) cur = i; });
  const [curStart, , , curDur] = LAYERS[cur];
  const p = (tt - curStart) / curDur;
  const visible = p < 1 && cur > 0 ? [cur - 1, cur] : [cur];

  const fg = fingerAt(tt, taps);

  return (
    <section
      ref={stageRef}
      className={`qpf${teaser ? ' qpf--teaser' : ''}${resetting ? ' is-resetting' : ''}`}
      aria-hidden={teaser ? 'true' : undefined}
      aria-roledescription={teaser ? undefined : 'animation'}
      aria-label="Split Check for Oracle QR Pay, shown on a phone: a diner opens the check, splits it equally, covers a friend, adds a 20% tip, pays by card and sees the payment approved."
    >
      <TapMap ch={size.ch} onMeasure={setTaps} />
      <div className="qpf-glow" style={{ opacity: 0.6 + 0.4 * fade(tt, GLOW, 1) }} />
      <div className="qpf-scene">
        <div className="qpf-shadow" style={{ transform: `translate3d(0, ${y}px, 0) scale(${s})`, opacity: 1 - clamp(cam.y / 60) }} aria-hidden="true" />
        <div
          ref={deviceRef}
          className="qpf-device"
          style={{ transform: `perspective(2200px) translate3d(0, ${y}px, 0) scale(${s}) rotateX(${cam.rx}deg) rotateY(${cam.ry}deg)` }}
          role="img"
          aria-label="QR Pay on an iPhone"
        >
          <div className="qpf-device__frame">
            <div ref={screenRef} className="qpf-device__screen">
              {visible.map((i) => {
                const [start, key] = LAYERS[i];
                const [, , curType] = LAYERS[cur];
                const isCur = i === cur;
                const style = isCur ? layerStyle(curType, (tt - start) / LAYERS[cur][3]) : underStyle(curType, p);
                const dim = !isCur && curType === 'push' ? 0.1 * navEase(clamp(p)) : 0;
                return (
                  <div key={key} className="qpf-layer" style={style}>
                    <div className="qpf-canvas" style={{ height: size.ch, transform: `scale(${size.k})` }}>
                      {renderScreen(key, st)}
                    </div>
                    {dim > 0 ? <div className="qpf-dim" style={{ opacity: dim }} /> : null}
                  </div>
                );
              })}
              {LAYERS[cur][1] !== 'camera' && !(LAYERS[cur][2] === 'launch' && p < 1) ? (
                <div className="qpf-chrome" aria-hidden="true">
                  <div className="qpf-canvas" style={{ height: size.ch, transform: `scale(${size.k})` }}>
                    <SafariChrome progress={tt < 4.6 ? st.load : null} />
                  </div>
                </div>
              ) : null}
              {fg && fg.a > 0.01 ? (
                <div className="qpf-finger" aria-hidden="true" style={{ transform: `translate3d(${fg.x * size.k}px, ${fg.y * size.k}px, 0)`, opacity: fg.a }}>
                  {fg.ripple != null ? <span className="qpf-finger__ripple" style={{ opacity: 1 - fg.ripple, transform: `scale(${1 + fg.ripple * 1.6})` }} /> : null}
                  <span className="qpf-finger__dot" style={{ transform: `scale(${1 - 0.22 * fg.press})` }} />
                </div>
              ) : null}
            </div>
          </div>
          <div className="qpf-device__island" />
        </div>
      </div>

      {teaser ? null : (
      <div className="qpf-bar">
        <button type="button" className="qpf-bar__play" onClick={() => setPlaying((v) => !v)} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? (
            <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><rect x="5.5" y="4.5" width="3" height="11" rx=".9" fill="currentColor" /><rect x="11.5" y="4.5" width="3" height="11" rx=".9" fill="currentColor" /></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true"><path d="M6.5 4.6v10.8L15.4 10 6.5 4.6Z" fill="currentColor" /></svg>
          )}
        </button>
        <ol className="qpf-chaps">
          {CHAPTERS.map(([label, start], i) => {
            const end = i < CHAPTERS.length - 1 ? CHAPTERS[i + 1][1] : D;
            const on = t >= start && t < end;
            return (
              <li key={label}>
                <button type="button" className={`qpf-chap${on ? ' is-active' : ''}`} aria-current={on ? 'step' : undefined}
                  aria-label={`Chapter ${i + 1}: ${label}`} onClick={() => seek(Math.max(0, start) + 0.01)}>
                  <span className="qpf-chap__track"><span className="qpf-chap__fill" style={{ width: `${clamp((t - start) / (end - start)) * 100}%` }} /></span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      )}
    </section>
  );
}
